
import { NumberFormatterService } from "@/core/services";
import { useWorkspace } from "@/hooks/workspace";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import { SkeletonCell } from "@/presentation/ui/components/common";
import { Division, Highlighter, Span } from "@ir/ui";

const WRAPPED_TEXT_CELL_STYLE = { wordBreak: "break-word", whiteSpace: "normal" } as const;

interface PromoProductUpcItemCellProps{
    promoProductUpcItemNumber: string | null | undefined;
    searchKeywords: string[];
    isRefreshing: boolean;
}

const renderPromoProductUpcItem = (promoProductUpcItemNumber: string, searchKeywords: string[], isRefreshing: boolean) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }
  if (promoProductUpcItemNumber) {
    return (
      <Highlighter keywords={searchKeywords}>
        <Division style={WRAPPED_TEXT_CELL_STYLE}>{promoProductUpcItemNumber}</Division>
      </Highlighter>
    );
  }

  return <Span>—</Span>;
};

export const PromoProductUpcItemCell = ({ promoProductUpcItemNumber, searchKeywords, isRefreshing }: PromoProductUpcItemCellProps) => {
  return renderPromoProductUpcItem(promoProductUpcItemNumber ?? "", searchKeywords, isRefreshing);
}

interface PromoProductNameCellProps {
    promoProductName: string | null | undefined;
    searchKeywords: string[];
    isRefreshing: boolean;
}

const renderPromoProductName = (promoProductName: string, searchKeywords: string[]) => {
  if (promoProductName) {
    return (
      <Highlighter keywords={searchKeywords}>
        <Division style={WRAPPED_TEXT_CELL_STYLE}>{promoProductName}</Division>
      </Highlighter>
    );
  }

  return <Span>—</Span>;
};

export const PromoProductNameCell = ({ promoProductName, searchKeywords, isRefreshing }: PromoProductNameCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  return renderPromoProductName(promoProductName ?? "", searchKeywords);
}

interface PromoProductCategoryCellProps {
    promoProductCategoryName: string | null | undefined;
    isRefreshing: boolean;
}

export const PromoProductCategoryCell = ({ promoProductCategoryName, isRefreshing }: PromoProductCategoryCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  if (promoProductCategoryName) {
    return <Division style={WRAPPED_TEXT_CELL_STYLE}>{promoProductCategoryName}</Division>;
  }

  return <Span>—</Span>;
}

interface PromoProductDiscountPercentageCellProps {
  promoProductDiscountPercentage: number | null | undefined;
  isRefreshing: boolean;
}

export const PromoProductDiscountPercentageCell = ({ promoProductDiscountPercentage, isRefreshing }: PromoProductDiscountPercentageCellProps) => {
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  if (promoProductDiscountPercentage) {
    return <Span>{promoProductDiscountPercentage}</Span>;
  }

  return <Span>—</Span>;
}

interface PromoProductDiscountAmountCellProps {
  promoProductDiscountAmount: number | null | undefined;
  isRefreshing: boolean;
}

export const PromoProductDiscountAmountCell = ({ promoProductDiscountAmount, isRefreshing }: PromoProductDiscountAmountCellProps) => {
  const { settings: workspaceSettings } = useWorkspace();
  if (isRefreshing) {
    return <SkeletonCell />;
  }

  if (promoProductDiscountAmount) {
    return <Span>{(workspaceSettings?.displayNumberFormat ? new NumberFormatterService(new NumberFormatterAdapter()).format(promoProductDiscountAmount, workspaceSettings.displayNumberFormat) : promoProductDiscountAmount)}</Span>;
  }

  return <Span>—</Span>;
}
